import { useState } from 'react';
import { sportsbookColourMapping } from '../pages/evscreen/EvScreenTable/SportsbookColourMapping';
import external_link_icon from '../assets/images/external_link_icon.svg';

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16)
	} : null;
};

// Helper function to lighten a color
const lightenColor = (hex, percent) => {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const amount = percent / 100;
	return `rgb(${Math.round(rgb.r + (255 - rgb.r) * amount)}, ${Math.round(rgb.g + (255 - rgb.g) * amount)}, ${Math.round(rgb.b + (255 - rgb.b) * amount)})`;
};

// Helper function to darken a color
const darkenColor = (hex, percent) => {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const amount = percent / 100;
	return `rgb(${Math.round(rgb.r * (1 - amount))}, ${Math.round(rgb.g * (1 - amount))}, ${Math.round(rgb.b * (1 - amount))})`;
};

// Helper function to create gradient from base color
const createGradient = (baseColor, isHovered) => {
	if (!baseColor || baseColor === 'this is like 5 diff books') {
		// Fallback to default blue/gold
		return isHovered 
			? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 25%, #d97706 50%, #f59e0b 75%, #fbbf24 100%)'
			: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 25%, #1d4ed8 50%, #2563eb 75%, #3b82f6 100%)';
	}
	
	const light = lightenColor(baseColor, isHovered ? 15 : 20);
	const base = baseColor;
	const dark = darkenColor(baseColor, isHovered ? 20 : 30);
	const darker = darkenColor(baseColor, isHovered ? 30 : 40);
	
	return isHovered
		? `linear-gradient(135deg, ${light} 0%, ${base} 25%, ${dark} 50%, ${base} 75%, ${light} 100%)`
		: `linear-gradient(135deg, ${light} 0%, ${base} 25%, ${darker} 50%, ${base} 75%, ${light} 100%)`;
};

// Helper function to create glow color from base
const createGlowColor = (baseColor, isHovered) => {
	if (!baseColor || baseColor === 'this is like 5 diff books') {
		return isHovered ? 'rgba(251, 191, 36, 0.6)' : 'rgba(59, 130, 246, 0.5)';
	}
	const rgb = hexToRgb(baseColor);
	if (!rgb) return 'rgba(59, 130, 246, 0.5)';
	return isHovered 
		? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`
		: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`;
};

export const BetLinkButton = (props) => {
	const { link, mobile, size = 'normal', sportsbook } = props;
	const [isHovered, setIsHovered] = useState(false);
	const isLarge = size === 'large' || mobile;
	
	// Get sportsbook color for desktop
	const baseColor = sportsbook ? (sportsbookColourMapping[sportsbook] || sportsbookColourMapping[sportsbook.trim()] || null) : null;
	const gradient = createGradient(baseColor, isHovered);
	const glowColor = createGlowColor(baseColor, isHovered);
	
	// Mobile: simple original style
	if (mobile) {
		return (
			<div 
				onClick={(e) => {
					e.stopPropagation()
				}} 
				className="cursor-pointer" 
				style={{ 
					width: "max-content", 
					alignContent: 'center', 
					backgroundColor: '#1e6ffc', 
					padding: isLarge ? '10px 16px' : '8px 14px', 
					borderRadius: '8px', 
					border: "1px solid #1e6ffc", 
					color: '#ffffff'
				}}
			>
				<a 
					href={link} 
					target="_new" 
					rel="noopener noreferrer"
					style={{ 
						textDecoration: 'none', 
						color: 'inherit'
					}}
				>
					<div 
						style={{
							backgroundColor: 'unset', 
							alignItems: "center", 
							fontWeight: '600',
							fontSize: isLarge ? '14px' : '12px'
						}} 
						className="flex flex-row items-center gap-1"
					>
						Bet
						<img 
							style={{backgroundColor: 'unset'}} 
							width={isLarge ? 16 : 14} 
							height={isLarge ? 16 : 14} 
							src={external_link_icon} 
							alt="external link icon" 
						/>
					</div>
				</a>
			</div>
		);
	}
	
	// Desktop: fancy casino-style
	return (
		<div 
			onClick={(e) => {
				e.stopPropagation()
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			className="cursor-pointer text-white font-bold rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-1 relative overflow-hidden"
			style={{ 
				fontSize: isLarge ? '14px' : '12px',
				minWidth: 'fit-content',
				padding: isLarge ? '9px 14px' : '10px 14px',
				background: gradient,
				boxShadow: isHovered 
					? `0 0 20px ${glowColor}, 0 0 40px ${glowColor}, 0 4px 15px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.25)`
					: `0 0 15px ${glowColor}, 0 0 30px ${glowColor}, 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)`,
				border: 'none',
				position: 'relative',
				zIndex: 20,
			}}
		>
			{/* Subtle sparkle effect - only on hover */}
			{isHovered && (
				<div
					style={{
						position: 'absolute',
						top: '25%',
						left: '35%',
						width: '3px',
						height: '3px',
						background: 'rgba(255, 255, 255, 0.8)',
						borderRadius: '50%',
						boxShadow: '0 0 6px rgba(255, 255, 255, 0.6)',
						animation: 'sparkle 2s ease-in-out infinite',
					}}
				/>
			)}
			{isHovered && (
				<div
					style={{
						position: 'absolute',
						top: '65%',
						left: '65%',
						width: '2px',
						height: '2px',
						background: 'rgba(255, 255, 255, 0.7)',
						borderRadius: '50%',
						boxShadow: '0 0 4px rgba(255, 255, 255, 0.5)',
						animation: 'sparkle 2.2s ease-in-out infinite 0.4s',
					}}
				/>
			)}
			<a 
				href={link} 
				target="_new" 
				rel="noopener noreferrer"
				style={{ 
					textDecoration: 'none', 
					color: 'inherit', 
					display: 'flex', 
					alignItems: 'center', 
					gap: '6px',
					position: 'relative',
					zIndex: 1,
				}}
			>
				<span style={{ 
					textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
					letterSpacing: '0.5px',
					fontWeight: '800',
				}}>Bet</span>
			</a>
			<style>{`
				@keyframes sparkle {
					0%, 100% {
						opacity: 0;
						transform: scale(0.8);
					}
					50% {
						opacity: 1;
						transform: scale(1.2);
					}
				}
			`}</style>
		</div>
	);
};
